/**
 * HrisConnectDialog — connect / test / sync an HRIS provider (Workday,
 * BambooHR). Distinct from the messaging-provider dialog in Connectors.tsx
 * because HRIS uses its own provider-agnostic endpoints
 * (/v1/integrations/hris/*) and has Test + Sync-now actions messaging lacks.
 *
 * Driven by the catalog ProviderDefinition.configFields, so one dialog serves
 * every HRIS provider — adding a future connector (ADP/Rippling) needs only a
 * catalog entry + backend provider, no dialog change.
 *
 * Flow: fill credential fields → Connect (POST connections, stores encrypted)
 * → Test (verifies upstream creds, shows worker count) → Sync now (manual
 * directory import). Secrets are never read back — a connected provider shows
 * its non-secret identity (subdomain / tenant) + a "credentials saved" state.
 */
import React, { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  hrisApi,
  type HrisConnectionResponse,
} from '../../api/integrations';
import type { ProviderDefinition } from './integrationCatalog';
import { ApiError } from '../../api/client';
import { translateApiError } from '../../i18n/translateApiError';

interface Props {
  provider: ProviderDefinition | null;
  connection: HrisConnectionResponse | null;
  onClose: () => void;
  onChanged: () => void; // refetch connections after connect/disconnect/sync
}

const HrisConnectDialog: React.FC<Props> = ({ provider, connection, onClose, onChanged }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  // ADR-039 two-way: local mirror of outbound_enabled (optimistic toggle).
  const [outboundEnabled, setOutboundEnabled] = useState(false);
  const [outboundResult, setOutboundResult] = useState<string | null>(null);

  React.useEffect(() => {
    // Reset transient state whenever the dialog opens for a provider.
    setValues({});
    setError(null);
    setTestResult(null);
    setOutboundResult(null);
    setOutboundEnabled(!!connection?.outbound_enabled);
  }, [provider?.key, connection?.outbound_enabled]);

  if (!provider) return null;

  const isConnected = !!connection;
  // ADR-039: only write-capable HRIS providers expose the two-way toggle.
  // BambooHR implements push_worker; Workday is inbound-only. (Matches the
  // backend supports_write gate — a Workday toggle attempt 400s.)
  const supportsWrite = provider.key === 'bamboohr';
  const allRequiredFilled = provider.configFields
    .filter((f) => f.required)
    .every((f) => (values[f.key] ?? '').trim().length > 0);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleConnect = () =>
    run(async () => {
      await hrisApi.connect(provider.key, values);
      setTestResult(null);
      onChanged();
    });

  const handleTest = () =>
    run(async () => {
      const r = await hrisApi.test(provider.key);
      setTestResult({ ok: r.ok, message: r.message });
      onChanged();
    });

  const handleSync = () =>
    run(async () => {
      await hrisApi.syncNow(provider.key);
      onChanged();
    });

  const handleDisconnect = () =>
    run(async () => {
      await hrisApi.disconnect(provider.key);
      onChanged();
      onClose();
    });

  const handleToggleOutbound = (enabled: boolean) =>
    run(async () => {
      const updated = await hrisApi.setOutbound(provider.key, enabled);
      setOutboundEnabled(!!updated.outbound_enabled);
      onChanged();
    });

  const handlePushOutbound = () =>
    run(async () => {
      const r = await hrisApi.outboundSyncNow(provider.key, false);
      const d = (r.details ?? {}) as { created?: number; updated?: number };
      setOutboundResult(
        t('integrations:hris.outboundResult', {
          created: d.created ?? 0,
          updated: d.updated ?? 0,
        }),
      );
      onChanged();
    });

  return (
    <Dialog open={!!provider} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('integrations:hris.connectTitle', { name: provider.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t('integrations:hris.connectSubtitle')}
          </Typography>

          {isConnected ? (
            <Alert severity="success" variant="outlined">
              {t('integrations:hris.alreadyConnected', {
                detail: connection?.subdomain || connection?.tenant_name || provider.name,
              })}
            </Alert>
          ) : (
            provider.configFields.map((f) => (
              <TextField
                key={f.key}
                label={t(f.i18nKey)}
                placeholder={f.placeholder}
                type={f.type === 'password' ? 'password' : 'text'}
                value={values[f.key] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                fullWidth
                size="small"
                required={f.required}
                autoComplete="off"
              />
            ))
          )}

          {/* ADR-039 two-way: outbound write-back toggle + push-now.
              Shown only for write-capable providers (BambooHR) once connected. */}
          {isConnected && supportsWrite ? (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={outboundEnabled}
                    disabled={busy}
                    onChange={(e) => handleToggleOutbound(e.target.checked)}
                  />
                }
                label={t('integrations:hris.twoWayLabel')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {t('integrations:hris.twoWayHint')}
              </Typography>
              {outboundEnabled ? (
                <Button
                  onClick={handlePushOutbound}
                  disabled={busy}
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1, borderRadius: 1, textTransform: 'none' }}
                >
                  {t('integrations:hris.pushNow')}
                </Button>
              ) : null}
              {outboundResult ? (
                <Alert severity="success" variant="outlined" sx={{ mt: 1, py: 0.25, fontSize: '0.78rem' }}>
                  {outboundResult}
                </Alert>
              ) : null}
            </Box>
          ) : null}

          {testResult ? (
            <Alert severity={testResult.ok ? 'success' : 'warning'} variant="outlined">
              {testResult.message}
            </Alert>
          ) : null}
          {error ? <Alert severity="error" variant="outlined">{error}</Alert> : null}

          {provider.guideUrl ? (
            <Typography variant="caption" color="text.secondary">
              {t('integrations:hris.guideHint')}{' '}
              <a href={provider.guideUrl} target="_blank" rel="noreferrer">{provider.guideUrl}</a>
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pb: 2, flexWrap: 'wrap', gap: 1 }}>
        {isConnected ? (
          <>
            <Button onClick={handleDisconnect} disabled={busy} color="error" size="small">
              {t('integrations:hris.disconnect')}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleTest} disabled={busy} size="small">
              {t('integrations:hris.test')}
            </Button>
            <Button onClick={handleSync} disabled={busy} variant="contained" size="small">
              {t('integrations:hris.syncNow')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy} size="small">
              {t('common:actions.cancel')}
            </Button>
            <Button
              onClick={handleConnect}
              disabled={busy || !allRequiredFilled}
              variant="contained"
              size="small"
            >
              {t('integrations:hris.connect')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default HrisConnectDialog;
